import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ORG_STORAGE_KEY } from '../lib/api';
import { Button, ErrorState, Field, Modal, TextField } from '../components/ui';
import type { Vendor } from '../lib/models';

export function EditVendor({ vendor, orgId, onClose }: {vendor:Vendor;orgId:string;onClose:()=>void}) {
  const [name, setName] = useState(vendor.displayName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error|null>(null);
  const qc = useQueryClient();
  async function save() {
    setBusy(true); setError(null);
    try {
      if (localStorage.getItem(ORG_STORAGE_KEY) !== orgId) throw Error('Company changed. Reopen the vendor in the selected company.');
      const res = await fetch('/api/vendor-editing/' + encodeURIComponent(vendor.id), {
        method:'PATCH', credentials:'include', headers:{'Content-Type':'application/json','X-Org-Id':orgId},
        body:JSON.stringify({displayName:name.trim(),expectedDisplayName:vendor.displayName}),
      });
      const result = await res.json();
      if (!res.ok) throw Error(result.error || 'Unable to save vendor name.');
      await qc.invalidateQueries({queryKey:['vendors',orgId]});
      onClose();
    } catch (e) { setError(e instanceof Error ? e : Error('Unable to save vendor name.')); }
    finally { setBusy(false); }
  }
  return <Modal open onClose={()=>{if(!busy)onClose();}} title="Edit vendor">
    <form className="space-y-4" onSubmit={e=>{e.preventDefault();if(!busy)void save();}}>
      <Field label="Display name (required)"><TextField autoFocus required maxLength={200} disabled={busy} value={name} onChange={e=>setName(e.target.value)}/></Field>
      <p className="text-sm text-gray-500">Updates this vendor's name. Existing bills, payments and balances stay linked to this vendor.</p>
      {error&&<ErrorState error={error}/>}
      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={busy||!name.trim()}>{busy?'Saving…':'Save changes'}</Button>
        <Button type="button" variant="secondary" disabled={busy} onClick={onClose}>Cancel</Button>
      </div>
    </form>
  </Modal>;
}
